import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: 'consultation',
    lotAddress: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        inquiry_type: formData.inquiryType,
        lot_address: formData.lotAddress || null,
        message: formData.message
      });

      toast.success('Thank you! We\'ll be in touch within 24 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        inquiryType: 'consultation',
        lotAddress: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-[#262626] mb-6">
              Let's Build Together
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Ready to start your custom home journey? Contact us today to schedule a consultation, request a lot evaluation, or get answers to your questions.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="md:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-[#262626] mb-6">
                  Get In Touch
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  Whether you're ready to start your custom build or just exploring options, we're here to help. Reach out and let's discuss your vision.
                </p>
              </div>

              <Card className="border-2 border-gray-100">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#DAA520]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#DAA520]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#262626] mb-1">Phone</h3>
                      <a href={`tel:${companyInfo.phone}`} className="text-gray-600 hover:text-[#DAA520] transition-colors">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#DAA520]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#DAA520]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#262626] mb-1">Email</h3>
                      <a href={`mailto:${companyInfo.email}`} className="text-gray-600 hover:text-[#DAA520] transition-colors break-all">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#DAA520]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#DAA520]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#262626] mb-1">Location</h3>
                      <p className="text-gray-600">{companyInfo.location}</p>
                      <p className="text-sm text-gray-500 mt-1">Serving Central Ohio</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#DAA520]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="text-[#DAA520]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#262626] mb-1">Business Hours</h3>
                      <p className="text-gray-600">Monday - Friday: 8am - 6pm</p>
                      <p className="text-gray-600">Saturday: 9am - 3pm</p>
                      <p className="text-gray-600">Sunday: By appointment</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-[#DAA520]/10 rounded-lg p-6">
                <h3 className="font-semibold text-[#262626] mb-2">Response Time</h3>
                <p className="text-gray-700">
                  We typically respond to all inquiries within <strong>24 hours</strong>. For urgent matters, please call us directly.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-3">
              <Card className="border-2 border-gray-100 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[#262626] mb-6">
                    Send Us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-[#262626] font-medium mb-2">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Doe"
                          className="border-gray-300 focus:border-[#DAA520] focus:ring-[#DAA520]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-[#262626] font-medium mb-2">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@example.com"
                          className="border-gray-300 focus:border-[#DAA520] focus:ring-[#DAA520]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-[#262626] font-medium mb-2">
                          Phone Number *
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="(614) 555-1234"
                          className="border-gray-300 focus:border-[#DAA520] focus:ring-[#DAA520]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inquiryType" className="text-[#262626] font-medium mb-2">
                          Inquiry Type *
                        </Label>
                        <select
                          id="inquiryType"
                          name="inquiryType"
                          value={formData.inquiryType}
                          onChange={handleChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#DAA520] focus:ring-[#DAA520] focus:outline-none"
                        >
                          <option value="consultation">Schedule Consultation</option>
                          <option value="lot-evaluation">Lot Evaluation</option>
                          <option value="floor-plan">Floor Plan Information</option>
                          <option value="pricing">Request Pricing</option>
                          <option value="investor">Investor Inquiry</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="lotAddress" className="text-[#262626] font-medium mb-2">
                        Lot Address (if applicable)
                      </Label>
                      <Input
                        id="lotAddress"
                        name="lotAddress"
                        value={formData.lotAddress}
                        onChange={handleChange}
                        placeholder="123 Main St, Columbus, OH 43215"
                        className="border-gray-300 focus:border-[#DAA520] focus:ring-[#DAA520]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#262626] font-medium mb-2">
                        Your Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder="Tell us about your project, timeline, budget, or any questions you have..."
                        className="border-gray-300 focus:border-[#DAA520] focus:ring-[#DAA520]"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#DAA520] hover:bg-[#B8860B] text-white py-6 text-lg font-semibold"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send className="ml-2" size={20} />
                    </Button>

                    <p className="text-sm text-gray-500 text-center">
                      By submitting this form, you agree to be contacted by EC Home Builds regarding your inquiry.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Map */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#262626] mb-4">
              Our Service Area
            </h2>
            <p className="text-xl text-gray-600">
              Proudly serving Central Ohio and surrounding communities
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <MapPin className="text-[#DAA520] mx-auto mb-4" size={64} />
                <h3 className="text-2xl font-bold text-[#262626] mb-2">Columbus, Ohio</h3>
                <p className="text-gray-600 max-w-md">
                  Central Ohio including Columbus, Delaware, Westerville, Dublin, Powell, Lewis Center, and surrounding areas
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#262626] mb-4">
              What Would You Like To Do?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Download Floor Plan Catalog',
                description: 'Browse all our floor plans and find the perfect design for your lot',
                action: 'Download Catalog',
                link: '#'
              },
              {
                title: 'Get a Cost Estimate',
                description: 'Use our calculator to estimate your custom build costs',
                action: 'Calculate Costs',
                link: '/cost-estimate'
              },
              {
                title: 'View Our Portfolio',
                description: 'See examples of our completed custom homes',
                action: 'View Portfolio',
                link: '/portfolio'
              }
            ].map((item, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#DAA520] transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#262626] mb-3">{item.title}</h3>
                  <p className="text-gray-600 mb-4">{item.description}</p>
                  <Button variant="outline" className="w-full border-[#DAA520] text-[#DAA520] hover:bg-[#DAA520] hover:text-white">
                    {item.action}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
